/* ==========================================================================
   DISPATCH LINE - works out the next dispatch day.

   WHY THIS IS NOT DONE IN LIQUID: Liquid renders once and Shopify caches
   product pages, so a date baked in at render time can still be served days
   later, promising a dispatch day that has already passed. This recomputes on
   every load.

   THE CLOCK IS THE WORKSHOP'S. Everything is evaluated in Europe/London through
   Intl, so a customer in New York at 9pm still sees the London working day. The
   device clock supplies the instant; the timezone is forced.

   THE GUARD: an out of stock variant never gets a date. Checked here as well as
   in the Liquid, because Dawn re-renders this markup on variant change and a
   variant that is sold out must not inherit the previous one's promise.
   ========================================================================== */
(function () {
  'use strict';

  var DAY = 86400000;

  /* Parts of "now" as they are in London, whatever the device is set to. */
  function londonParts(d) {
    var f = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    var out = {};
    f.formatToParts(d).forEach(function (p) { out[p.type] = p.value; });
    return out;
  }

  var WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  /* P169 (26 Sep 2026): the date is named in the page language; /de printed
     "MON 28 SEP". English keeps en-GB, so its output is unchanged. londonParts
     above stays en-GB: WD reads its English weekday names. */
  var PAGE_LANG = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
  var DATE_LOCALE = PAGE_LANG && PAGE_LANG !== 'en' ? PAGE_LANG : 'en-GB';

  function fmt(y, m, day) {
    /* Build in UTC purely to name the day; no timezone shifting is implied. */
    var d = new Date(Date.UTC(y, m - 1, day));
    var opts = { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' };
    try {
      return new Intl.DateTimeFormat(DATE_LOCALE, opts).format(d);
    } catch (e) {
      return new Intl.DateTimeFormat('en-GB', opts).format(d);
    }
  }

  function iso(y, m, day) {
    return y + '-' + String(m).padStart(2, '0') + '-' + String(day).padStart(2, '0');
  }

  function compute(el) {
    var target = el.querySelector('[data-stk-dispatch-date]');
    if (!target) return;
    if (el.dataset.mode === 'lead') return;
    if (el.dataset.inStock !== 'true') return;

    var cutoff = parseInt(el.dataset.cutoff, 10);
    if (isNaN(cutoff)) cutoff = 10;

    var days = (el.dataset.days || '1,2,3,4,5')
      .split(',').map(function (x) { return parseInt(x.trim(), 10); })
      .filter(function (x) { return !isNaN(x); });
    if (!days.length) days = [1, 2, 3, 4, 5];

    var closed = (el.dataset.closed || '')
      .split(',').map(function (x) { return x.trim(); }).filter(Boolean);

    var p = londonParts(new Date());
    var y = parseInt(p.year, 10), m = parseInt(p.month, 10), d = parseInt(p.day, 10);
    var dow = WD[p.weekday];
    var hour = parseInt(p.hour, 10);
    var minute = parseInt(p.minute, 10);

    /* Past the cut-off, today is gone. On or before it, today still counts,
       but only if today is a working day and not a closed date. */
    var pastCutoff = hour > cutoff || (hour === cutoff && minute > 0);
    var cursor = Date.UTC(y, m - 1, d);
    if (pastCutoff) cursor += DAY;

    for (var i = 0; i < 400; i++) {
      var c = new Date(cursor);
      var cy = c.getUTCFullYear(), cm = c.getUTCMonth() + 1, cd = c.getUTCDate();
      var cdow = c.getUTCDay();
      if (days.indexOf(cdow) !== -1 && closed.indexOf(iso(cy, cm, cd)) === -1) {
        var text = fmt(cy, cm, cd);
        /* ONLY WRITE ON CHANGE. Writing textContent is itself a childList
           mutation, so an unconditional write inside the observer re-triggered
           the observer, which wrote again, for ever. Harmless-looking on a
           static page; in the theme editor, which mutates the DOM constantly,
           it pegged the CPU and hung the editor. Found 29 Aug 2026. */
        if (target.textContent !== text) target.textContent = text;
        return;
      }
      cursor += DAY;
    }
  }

  var observer = null;
  var running = false;

  function run() {
    /* Re-entrancy guard. Even with the change check above, nothing here should
       be able to drive itself. */
    if (running) return;
    running = true;
    if (observer) observer.disconnect();
    try {
      document.querySelectorAll('[data-stk-dispatch]').forEach(compute);
    } finally {
      if (observer) {
        observer.observe(document.body, { childList: true, subtree: true });
      }
      running = false;
    }
  }

  /* Dawn does NOT replace this markup on a variant change: product-info.js
     swaps only the price, SKU, inventory and volume-pricing elements, so the
     strip kept the page-load variant's stock state and sold count (Thomas,
     26 Sep 2026: "its not updating on variant flick, only page load"). Dawn
     does fetch the whole section for the new variant and hands the parsed
     document to variantChange subscribers, so lift our freshly rendered strip
     out of that and swap it in. Liquid stays the single source of the markup;
     the observer below then recomputes the date. */
  function swapFromVariantChange(event) {
    var html = event && event.data && event.data.html;
    if (!html || typeof html.querySelectorAll !== 'function') return;
    var fresh = html.querySelectorAll('[data-stk-dispatch]');
    var current = document.querySelectorAll('[data-stk-dispatch]');
    if (!fresh.length || fresh.length !== current.length) return;
    current.forEach(function (el, i) {
      el.replaceWith(document.importNode(fresh[i], true));
    });
    schedule();
  }

  function listenForVariantChange() {
    /* pubsub.js declares PUB_SUB_EVENTS with const, so it is a global binding
       but NOT a window property: test the bare identifiers. */
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return false;
    subscribe(PUB_SUB_EVENTS.variantChange, swapFromVariantChange);
    return true;
  }

  /* Re-run on any DOM change rather than binding once to the element, so the
     swapped-in strip gets its date computed too. */
  /* Coalesce bursts of mutations into one pass. The theme editor can fire
     hundreds in a row; recomputing on each is wasted work. */
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; run(); });
  }

  function start() {
    observer = new MutationObserver(schedule);
    run();
    /* pubsub.js is deferred from the layout head, so it has normally run by
       now; if not, try once more when the document is ready. */
    if (!listenForVariantChange()) {
      window.addEventListener('load', listenForVariantChange, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
